import { type ApplicationPosition as BaseApplicationPosition, useApplicationPositionCalculator } from './applicationPosition';
import { calcPriceNdsByPrice } from './nds';
import Decimal from "decimal.js-light";

interface Application {
    offerPrice: string;
    [key: string]: any;
}

interface ApplicationPosition extends BaseApplicationPosition {
    quantity: string;
    fullQuanity?: string;
}

interface ApplicationCalculator {
    getTotalCost: () => string,
    getTotalCostNds: () => string,
    getChosenCost: () => string,
    getChosenCostNds: () => string,
}

const useApplicationWithPositionsCalculator = (
  positions: ApplicationPosition[],
  ndsRate?: string,
  priceField?: string,
) : ApplicationCalculator => {
    const totalQuantityGetter = (position: ApplicationPosition) => position.fullQuantity ?? position.quantity;
    const chosenQuantityGetter = (position: ApplicationPosition) => position.quantity;
    const getCalculator = (position: ApplicationPosition) => useApplicationPositionCalculator(position, ndsRate, priceField);

    const getCost = (
      costGetter: (position: ApplicationPosition) => (quantity: string) => string,
      quantityGetter: (position: ApplicationPosition) => string,
    ) : string => {
        return positions.reduce(
          (carry: string, position: ApplicationPosition) => {
            const positionCost = costGetter(position)(quantityGetter(position));
            return new Decimal(carry).plus(new Decimal(positionCost)).toString();
          },
          '0'
        );
    };

    return {
        getTotalCost: () => getCost(
          (position) => getCalculator(position).getCostForQuantity,
          totalQuantityGetter,
        ),
        getTotalCostNds: () => getCost(
          (position) => getCalculator(position).getCostNdsForQuantity,
          totalQuantityGetter,
        ),
        getChosenCost: () => getCost(
          (position) => getCalculator(position).getCostForQuantity,
          chosenQuantityGetter,
        ),
        getChosenCostNds: () => getCost(
          (position) => getCalculator(position).getCostNdsForQuantity,
          chosenQuantityGetter,
        ),
    };
}

const useApplicationWithoutPositionsCalculator = (
  application: Application,
  ndsRate?: string,
) : ApplicationCalculator => {
    const getTotalCost = (): string => application.offerPrice;
    const getTotalCostNds = (): string => calcPriceNdsByPrice(getTotalCost(), ndsRate);

    return {
        getTotalCost,
        getTotalCostNds,
        getChosenCost: getTotalCost,
        getChosenCostNds: getTotalCostNds,
    };
}

export const useApplicationCalculator = (
  application: Application,
  positions: ApplicationPosition[],
  ndsRate?: string,
  priceField?: string,
) : ApplicationCalculator =>
  positions?.length > 0
    ? useApplicationWithPositionsCalculator(positions, ndsRate, priceField)
    : useApplicationWithoutPositionsCalculator(application, ndsRate)
